import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import * as React from 'react';
import { AppState, useAppStateContext } from 'src/appState/AppState';
import { EventRowMobxWrapper } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowMobx';
import { EventGroupHeader } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventGroupHeader';
import { EventListWrapper } from 'src/domains/sportsbook/webview/components/eventsList/EventList.style';
import { observer } from 'src/utils/mobx-react';
import {
    EventsGroupViewCompetition,
    EventsGroupViewSport,
} from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupView';
import { EventsGroupState } from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupState';
import { EventsListState } from 'src/domains/sportsbook/state/eventsListState/EventsListState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { SpecialSportsState } from 'src/domains/sportsbook/state/specialSportsState/SpecialSportsState';
import { CompetitionWrapper } from './EventRowMobx.style';
import { Common, useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';

const DEFAULT_OPEN_GROUPS = 5;

const renderCompetition = (common: Common, competitionGroup: EventsGroupViewCompetition): Array<React.ReactNode> => {
    const events = competitionGroup.events;
    const out = [];

    if (events.length > 0) {
        for (const event of events) {
            out.push(
                <EventRowMobxWrapper
                    key={`event_${event.id}`}
                    smallEvent={event}
                    competitionId={AppSportsBookState.get(common).models.id.getCompetitionId(competitionGroup.id)}
                />
            );
        }
    }

    return out;
};

const renderSportGroup = (
    common: Common,
    sportGroup: EventsGroupViewSport,
    eventsListState: EventsListState,
    sportsbookVariant: DefaultComponentVariantType | null
): Array<React.ReactNode> => {
    const out = [];

    for (const competition of sportGroup.competitions) {
        const lastSelectionsHeader = eventsListState.lastSelectionsHeader;
        const competitionName = competition.name;
        const isVisible = competition.visible;

        out.push(
            <EventGroupHeader
                competitionId={AppSportsBookState.get(common).models.id.getCompetitionId(competition.id)}
                dataTest='competition-header'
                key={`group_${competition.id}`}
                label={competitionName}
                onToggle={competition.toggle}
                open={isVisible}
                selections={lastSelectionsHeader}
            />
        );

        if (isVisible) {
            const outCompetition =
                sportsbookVariant === DefaultComponentVariantType.SECONDARY ? (
                    <CompetitionWrapper>{renderCompetition(common, competition)}</CompetitionWrapper>
                ) : (
                    renderCompetition(common, competition)
                );
            out.push(outCompetition);
        }
    }

    return out;
};

class State {
    public readonly appState: AppState;
    public readonly groupEvents: MobxMapAutoNew<EventsCollectionQueryModel | null, EventsGroupState>;

    public constructor(appState: AppState, specialSportsState: SpecialSportsState) {
        this.appState = appState;

        this.groupEvents = new MobxMapAutoNew((eventsCollection) => {
            const { languagesState } = this.appState;

            return new EventsGroupState(
                DEFAULT_OPEN_GROUPS,
                languagesState.getTranslation,
                eventsCollection,
                specialSportsState
            );
        });
    }
}

const EventsListMobxContent = observer('EventsListMobxContent', (props: EventsListPropsType) => {
    const appState = useAppStateContext();
    const common = useCommon();
    const appSportsBookState = AppSportsBookState.get(common);
    const configState = ConfigComponents.get(common).config;
    const { sportsbookVariant } = configState.componentVariants;
    const specialSportsState = appSportsBookState.specialSportsMap.get(props.sportId);
    const [state] = React.useState(() => new State(appState, specialSportsState));
    const listToView = state.groupEvents.get(props.eventsListState.events).listToView;

    return (
        <EventListWrapper variant={sportsbookVariant}>
            {listToView.sports.map((item) => renderSportGroup(common, item, props.eventsListState, sportsbookVariant))}
        </EventListWrapper>
    );
});

interface EventsListPropsType {
    eventsListState: EventsListState;
    sportId: string | null;
}

export const EventsListMobx = observer('EventsListMobx', (props: EventsListPropsType) => {
    const filters_coupon = props.eventsListState.filter_coupon;
    return (
        <EventsListMobxContent
            key={filters_coupon}
            eventsListState={props.eventsListState}
            sportId={props.sportId}
        />
    );
});
